import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Scale, Plus, TrendingUp, TrendingDown, Building2, AlertCircle } from 'lucide-react';
import { Button, CardStats, SearchInput, ExportDropdown, TableHeader } from '@components/ui';
import { TablePageSkeleton } from '@components/skeleton';
import { useExportExcel } from '@hooks';
import {
    getCariAccountsByConstructionId,
    getCariAccounts,
    getCariStats,
    mockCariAccounts,
} from '@mocks/cariHesaplar';
import CariHesaplarTablosu from './CariHesaplarTablosu';
import CariHesapDetay from './CariHesapDetay';
import HareketModal from './HareketModal';
import clsx from 'clsx';

const formatCurrency = (amount) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);

export default function CariHesaplar() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'supplier' | 'subcontractor' | 'service'
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'open' | 'settled'
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [hareketModalOpen, setHareketModalOpen] = useState(false);
    const [isLoading] = useState(false);

    // Export
    const { exportToExcel, isExporting } = useExportExcel();

    // Veri çekimi
    const allAccounts = useMemo(() =>
        constructionId
            ? getCariAccountsByConstructionId(constructionId)
            : getCariAccounts(),
        [constructionId]
    );

    // İstatistikler
    const stats = useMemo(() => getCariStats(constructionId), [constructionId]);

    // Filtreleme
    const filteredAccounts = useMemo(() => {
        return allAccounts.filter(account => {
            // Tip filtresi
            if (typeFilter !== 'all' && account.companyType !== typeFilter) return false;
            // Durum filtresi
            if (statusFilter === 'open' && account.currentBalance === 0) return false;
            if (statusFilter === 'settled' && account.currentBalance > 0) return false;
            // Arama
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                return (
                    account.companyName.toLowerCase().includes(q) ||
                    account.companyFullName.toLowerCase().includes(q) ||
                    account.companyTypeLabel.toLowerCase().includes(q)
                );
            }
            return true;
        });
    }, [allAccounts, typeFilter, statusFilter, searchQuery]);

    const handleRowClick = (account) => {
        setSelectedAccount(account);
    };

    const handleExport = () => {
        exportToExcel({
            data: filteredAccounts.map(a => ({
                'Firma': a.companyFullName,
                'Tür': a.companyTypeLabel,
                'Toplam Alış': a.totalPurchases,
                'Toplam Ödeme': a.totalPayments,
                'Bakiye': a.currentBalance,
                'Son Hareket': a.lastMovementDate,
                'İşlem Sayısı': a.movementCount,
            })),
            columns: [
                { header: 'Firma', key: 'Firma' },
                { header: 'Tür', key: 'Tür' },
                { header: 'Toplam Alış', key: 'Toplam Alış', format: 'currency' },
                { header: 'Toplam Ödeme', key: 'Toplam Ödeme', format: 'currency' },
                { header: 'Bakiye', key: 'Bakiye', format: 'currency' },
                { header: 'Son Hareket', key: 'Son Hareket' },
                { header: 'İşlem Sayısı', key: 'İşlem Sayısı' },
            ],
            fileName: 'cari_hesaplar',
            sheetName: 'Cari Hesaplar',
        });
    };

    if (isLoading) return <TablePageSkeleton statsCount={4} tableRows={8} tableColumns={6} />;

    const typeFilters = [
        { key: 'all', label: 'Tümü' },
        { key: 'subcontractor', label: 'Taşeron' },
        { key: 'supplier', label: 'Tedarikçi' },
        { key: 'service', label: 'Hizmet' },
    ];

    const statusFilters = [
        { key: 'all', label: 'Tümü' },
        { key: 'open', label: 'Açık Hesap' },
        { key: 'settled', label: 'Kapalı' },
    ];

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 bg-error/10 rounded-lg flex items-center justify-center">
                            <Scale size={20} className="text-error" />
                        </div>
                        <h1 className="text-2xl font-bold text-text-primary">Cari Hesaplar</h1>
                    </div>
                    <p className="text-text-tertiary mt-1.5">
                        {showAllConstructions
                            ? 'Tüm inşaatlar — Tedarikçi ve taşeron borç takibi'
                            : `${selectedConstruction?.name || ''} — Satın alınan malzeme ve hizmet borç takibi`}
                    </p>
                </div>
                <Button
                    variant="primary"
                    icon={<Plus size={18} />}
                    onClick={() => setHareketModalOpen(true)}
                    className="shrink-0"
                >
                    Yeni Hareket
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <CardStats
                    title="Toplam Açık Bakiye"
                    value={formatCurrency(stats.totalBalance)}
                    icon={Scale}
                    color={stats.totalBalance > 0 ? 'error' : 'success'}
                    size="md"
                />
                <CardStats
                    title="Toplam Alış"
                    value={formatCurrency(stats.totalPurchases)}
                    icon={TrendingUp}
                    color="error"
                    size="md"
                />
                <CardStats
                    title="Toplam Ödeme"
                    value={formatCurrency(stats.totalPayments)}
                    icon={TrendingDown}
                    color="success"
                    size="md"
                />
                <CardStats
                    title="Açık Hesap"
                    value={`${stats.activeCount} firma`}
                    icon={AlertCircle}
                    color={stats.activeCount > 0 ? 'warning' : 'tertiary'}
                    size="md"
                />
            </div>

            {/* Filters + Table */}
            <div className="bg-bg-primary rounded-xl border border-border-light overflow-hidden">
                <TableHeader
                    title="Firma Cari Hesapları"
                    subtitle={
                        showAllConstructions
                            ? `${filteredAccounts.length} firma`
                            : `${selectedConstruction?.name || ''} • ${filteredAccounts.length} firma`
                    }
                    className="border-none"
                >
                    <SearchInput
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Firma ara..."
                        size="md"
                    />
                    <ExportDropdown onExportExcel={handleExport} isExporting={isExporting} />
                </TableHeader>

                {/* Filter Row */}
                <div className="px-4 pb-4 flex flex-wrap gap-3">
                    {/* Tip Filtresi */}
                    <div className="flex items-center gap-1 bg-bg-secondary rounded-lg p-1">
                        {typeFilters.map(f => (
                            <button
                                key={f.key}
                                onClick={() => setTypeFilter(f.key)}
                                className={clsx(
                                    'px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                                    typeFilter === f.key
                                        ? 'bg-bg-primary text-primary shadow-sm'
                                        : 'text-text-secondary hover:text-text-primary'
                                )}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* Durum Filtresi */}
                    <div className="flex items-center gap-1 bg-bg-secondary rounded-lg p-1">
                        {statusFilters.map(f => (
                            <button
                                key={f.key}
                                onClick={() => setStatusFilter(f.key)}
                                className={clsx(
                                    'px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                                    statusFilter === f.key
                                        ? 'bg-bg-primary text-primary shadow-sm'
                                        : 'text-text-secondary hover:text-text-primary'
                                )}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                <CariHesaplarTablosu
                    data={filteredAccounts}
                    onRowClick={handleRowClick}
                />

                {filteredAccounts.length === 0 && (
                    <div className="py-12 text-center">
                        <Scale size={40} className="mx-auto text-text-muted mb-3" />
                        <p className="text-text-tertiary">Kriterlere uygun cari hesap bulunamadı.</p>
                        {searchQuery && (
                            <button
                                className="mt-2 text-primary text-sm hover:underline"
                                onClick={() => setSearchQuery('')}
                            >
                                Aramayı temizle
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Detay Panel (Slide-over) */}
            {selectedAccount && (
                <CariHesapDetay
                    account={selectedAccount}
                    constructionId={constructionId}
                    onClose={() => setSelectedAccount(null)}
                    onMovementAdded={() => setSelectedAccount(null)}
                />
            )}

            {/* Yeni Hareket Modal */}
            <HareketModal
                isOpen={hareketModalOpen}
                onClose={() => setHareketModalOpen(false)}
                onSubmit={() => setHareketModalOpen(false)}
            />
        </div>
    );
}
